/**
 * `RenderScene` ↔ JSON snapshot serializer for snapshot-driven CLI
 * debugging.
 *
 * Scope is intentionally narrow: deserialize back into a `RenderScene`
 * shape **just rich enough for `diffScene` to compare prev vs. next**.
 * Fields the diff classifier ignores (full bindings, full plan internals,
 * subColumn PartialDates, layout fields beyond what `cellLayoutEqual`
 * compares) are stubbed.
 *
 * Use case: `--save-scene <path>` writes the snapshot, `--prev <path>`
 * loads it as the prior scene for the next render so the diff colors
 * highlight what changed across invocations. The deserialized scene is
 * never re-rendered — it only feeds `diffScene`.
 */

import type { Option } from "../core/option.ts";
import { DateTime, NaiveDate, NaiveDateTime, PartialDate, TimeOfDay, TimezoneRegion, type Utc } from "@tento-chrono";

import { cellValueFromJson, cellValueWire } from "../core/dtype.ts";
import { Json } from "../core/json.ts";
import type { CellLayout, ColumnKey } from "./layout-types.ts";
import { Timescale } from "./timescale.ts";
import type { FixedChildIntent } from "../providers/plugin-providers.ts";
import type { RenderBinding, RenderBindings } from "./render-plugin-core.ts";
import type { RenderPlan } from "./render-plan.ts";
import { RowKey } from "../ids/row-key.ts";
import { RowLocalId } from "../ids/row-local-id.ts";
import {
  type CellContent,
  type CellNode,
  type LegendNode,
  type RenderScene,
  type RowOverlayNode,
  type RowSection,
  SceneKey,
} from "./scene.ts";
import { CellId } from "../ids/ids.ts";
import { Role } from "../rows/descriptor.ts";
import { WireDecodeError } from "../wire/wire-decode.ts";
import { Model } from "@tento-core/model";

/**
 * Schema version — bump on incompatible shape changes. 2: a binding carries
 * its `rowRole` beside its `pluginRole` (version 1 held only the plugin's).
 */
const SCHEMA_VERSION = 2;

/**
 * A `RenderScene` as JSON, just rich enough for `diffScene` to compare two frames, written by the
 * CLI's snapshot debugging.
 */
export interface SceneSnapshot {
  readonly version: number;
  readonly columnRange: { readonly start: string; readonly end: string };
  readonly timescale: Timescale;
  readonly columns: readonly SerializedColumn[];
  readonly cells: readonly SerializedCell[];
  readonly rowOverlays: readonly SerializedOverlay[];
  readonly legends: readonly SerializedLegend[];
  /**
   * Frame revision: what the source scene carried (the live dual-run), `null`
   * for an unstamped scene.
   */
  readonly revision: Option<{ readonly tau: number; readonly epoch: number }>;
}

interface SerializedColumn {
  readonly offset: number;
  readonly pdStart: string; // ISO date for column.pd.start
  readonly pdEnd: string;   // ISO date for column.pd.end
  readonly rangeStart: string; // ISO datetime
  readonly rangeEnd: string;
}

interface SerializedCell {
  readonly rowKey: string;        // RowKey.toString()
  readonly columnOffset: number;
  readonly rowIndex: number;
  readonly columnIndex: number;
  readonly columnDate: string;    // ISO
  readonly bindings: SerializedBindings;
  readonly plan: SerializedPlan;
  readonly layout: Option<SerializedLayout>;
  readonly content: Option<SerializedContent>;
  /**
   * Output stamp — OPTIONAL. Emitted only for stamped cells (the live
   * dual-run); the key is omitted for stampless cells so legacy fixtures stay
   * byte-compatible. Round-trips so `diffScene`'s version fast-path still fires
   * on a deserialized prior scene. `attrVersion` is optional on READ (an older
   * artifact written before stamps carried attribute versions has none) and
   * defaults to `0` on deserialize —
   * exactly the additive/default-on-read discipline the stamp itself follows.
   */
  readonly stamp?: {
    readonly address: Option<string>;
    readonly valuesVersion: number;
    readonly attrVersion?: number;
  };
}

interface SerializedBindings {
  readonly pluginId: string;
  /** The binding's `pluginRole`, read back through `Role.of`. */
  readonly pluginRole: string;
  /** The binding's `rowRole` (the bound row's own role), read back through `Role.of`. */
  readonly rowRole: string;
  readonly contentType: string;
}

interface SerializedPlan {
  readonly mode: { type: string; spanTimescale?: string; spanCount?: number };
  readonly spanMode: { type: string };
  readonly descriptorType: string;
  readonly skipContent: boolean;
  readonly timeState: { inThePast: boolean; isCurrent: boolean; isFuture: boolean };
  readonly stackingSubcount: number;
}

interface SerializedLayout {
  readonly offsetPx: number;
  readonly cellHeightPx: number;
  readonly cellHeightWithChildrenPx: number;
  readonly stackingCellsPerColumn: Option<number>;
}

type SerializedContent =
  | { readonly kind: "none" }
  | { readonly kind: "value"; readonly entry: Option<SerializedEntry> };

interface SerializedEntry {
  readonly id: Option<string>;
  readonly date: string; // PartialDate.toString()
  /** The cell value as JSON (what the snapshot file holds). */
  readonly value: Json;
}

interface SerializedOverlay {
  readonly rowKey: string;
  readonly pluginId: string;
  readonly rowIndex: number;
  readonly bindings: SerializedBindings;
  readonly intents: readonly SerializedIntent[];
}

interface SerializedIntent {
  readonly id: string;
  readonly rangeStart: string;
  readonly rangeEnd: string;
  readonly pluginId: string;
  readonly align: Option<string>;
  readonly fillWidth: Option<boolean>;
  readonly isOverflow: Option<boolean>;
}

interface SerializedLegend {
  readonly rowKey: string;
  readonly rowIndex: number;
  readonly label: string;
  readonly bindings: SerializedBindings;
  readonly timescaleSetting: Timescale | "custom";
  readonly isSystemRow: boolean;
  readonly section: RowSection;
}

// ── Serialize / deserialize ────────────────────────────────────────

/** Values that go with `SceneSnapshot`: `fromScene`, `toScene`. */
export namespace SceneSnapshot {
  /** Build a snapshot from a live `RenderScene`. */
  export const fromScene = serializeScene;
  /** Best-effort reconstruction back into a `RenderScene` — just rich enough for `diffScene`. */
  export const toScene = deserializeScene;
}

function serializeScene<K extends ColumnKey>(
  scene: RenderScene<K>,
): SceneSnapshot {
  const columnsByOffset = new Map<number, K>();
  for (const cell of scene.cells.values()) {
    columnsByOffset.set(cell.columnIndex, cell.key.column as K);
  }
  const columns: SerializedColumn[] = [];
  for (const [offset, col] of [...columnsByOffset.entries()].sort((a, b) => a[0] - b[0])) {
    columns.push({
      offset,
      pdStart: col.pd.start.toString(),
      pdEnd: col.pd.end.toString(),
      rangeStart: col.range.start.toString(),
      rangeEnd: col.range.end.toString(),
    });
  }

  return {
    version: SCHEMA_VERSION,
    columnRange: {
      start: scene.columnRange.start.toString(),
      end: scene.columnRange.end.toString(),
    },
    timescale: scene.timescale,
    columns,
    cells: [...scene.cells.values()].map(serializeCell),
    rowOverlays: [...scene.rowOverlays.values()].map(serializeOverlay),
    legends: [...scene.legends.values()].map(serializeLegend),
    revision: scene.revision != null
      ? { tau: scene.revision.tau, epoch: scene.revision.epoch }
      : null,
  };
}

function serializeCell<K extends ColumnKey>(cell: CellNode<K>): SerializedCell {
  return {
    rowKey: cell.key.row.key.toString(),
    columnOffset: cell.key.column.offset,
    rowIndex: cell.rowIndex,
    columnIndex: cell.columnIndex,
    columnDate: cell.columnDate.toString(),
    bindings: serializeBindings(cell.bindings),
    plan: serializePlan(cell.plan),
    layout: cell.layout != null ? serializeLayout(cell.layout) : null,
    content: cell.content != null ? serializeContent(cell.content) : null,
    // Absent stamp ⇒ omit the key (byte-compat), never `null`-fill.
    ...(cell.stamp != null
      ? {
        stamp: {
          address: cell.stamp.address ?? null,
          valuesVersion: cell.stamp.valuesVersion,
          attrVersion: cell.stamp.attrVersion,
        },
      }
      : {}),
  };
}

function serializeBindings(b: RenderBindings): SerializedBindings {
  return {
    pluginId: b.cell.pluginId,
    pluginRole: b.cell.pluginRole,
    rowRole: b.cell.rowRole,
    contentType: b.cell.contentType,
  };
}

function serializePlan(plan: RenderPlan): SerializedPlan {
  const mode = plan.mode;
  const serializedMode: SerializedPlan["mode"] = mode.type === "span-stacked"
    ? { type: mode.type, spanTimescale: mode.spanTimescale, spanCount: mode.spanCount }
    : { type: mode.type };
  return {
    mode: serializedMode,
    spanMode: { type: plan.spanMode.type },
    descriptorType: plan.descriptorType,
    skipContent: plan.skipContent,
    timeState: {
      inThePast: plan.timeState.inThePast,
      isCurrent: plan.timeState.isCurrent,
      isFuture: plan.timeState.isFuture,
    },
    stackingSubcount: plan.stacking?.subColumns.length ?? 0,
  };
}

function serializeLayout(layout: CellLayout): SerializedLayout {
  return {
    offsetPx: layout.offsetPx,
    cellHeightPx: layout.cellHeightPx,
    cellHeightWithChildrenPx: layout.cellHeightWithChildrenPx,
    stackingCellsPerColumn: layout.stackingInfo?.cellsPerColumn ?? null,
  };
}

function serializeContent(content: CellContent): SerializedContent {
  if (content.kind === "none") return { kind: "none" };
  if (content.entry == null) return { kind: "value", entry: null };
  return {
    kind: "value",
    entry: {
      id: content.entry.id ?? null,
      date: content.entry.date.toString(),
      value: cellValueWire(content.entry.value) ?? null,
    },
  };
}

function serializeOverlay(overlay: RowOverlayNode): SerializedOverlay {
  return {
    rowKey: overlay.key.row.key.toString(),
    pluginId: overlay.key.pluginId,
    rowIndex: overlay.rowIndex,
    bindings: serializeBindings(overlay.bindings),
    intents: overlay.intents.map(serializeIntent),
  };
}

function serializeIntent(intent: FixedChildIntent): SerializedIntent {
  return {
    id: intent.id,
    rangeStart: intent.range.start.toString(),
    rangeEnd: intent.range.end.toString(),
    pluginId: intent.pluginId,
    align: intent.align ?? null,
    fillWidth: intent.fillWidth ?? null,
    isOverflow: intent.isOverflow ?? null,
  };
}

function serializeLegend(legend: LegendNode): SerializedLegend {
  return {
    rowKey: legend.key.row.key.toString(),
    rowIndex: legend.rowIndex,
    label: legend.label,
    bindings: serializeBindings(legend.bindings),
    timescaleSetting: legend.timescaleSetting,
    isSystemRow: legend.isSystemRow,
    section: legend.section,
  };
}

// ── Deserialize ────────────────────────────────────────────────────

function deserializeScene(
  snapshot: SceneSnapshot,
  registry: RowLocalId.Registry,
): RenderScene<ColumnKey> {
  if (snapshot.version !== SCHEMA_VERSION) {
    throw new WireDecodeError("SceneSnapshot", [
      Model.issue(
        ["version"],
        "version",
        `unsupported snapshot version ${snapshot.version} (expected ${SCHEMA_VERSION})`,
      ),
    ]);
  }
  const columns = new Map<number, ColumnKey>();
  for (const c of snapshot.columns) {
    columns.set(c.offset, deserializeColumn(c));
  }

  const cells = new Map<string, CellNode>();
  for (const [index, sc] of snapshot.cells.entries()) {
    const column = columns.get(sc.columnOffset);
    if (column == null) {
      throw new Error(`scene-serde: cell references unknown column offset ${sc.columnOffset}`);
    }
    const cell = deserializeCell(sc, column, registry, ["cells", String(index)]);
    cells.set(SceneKey.toString(cell.key), cell);
  }

  const rowOverlays = new Map<string, RowOverlayNode>();
  for (const [index, so] of snapshot.rowOverlays.entries()) {
    const overlay = deserializeOverlay(so, registry, ["rowOverlays", String(index)]);
    rowOverlays.set(SceneKey.toString(overlay.key), overlay);
  }

  const legends = new Map<string, LegendNode>();
  for (const [index, sl] of snapshot.legends.entries()) {
    const legend = deserializeLegend(sl, registry, ["legends", String(index)]);
    legends.set(SceneKey.toString(legend.key), legend);
  }

  return {
    columnRange: new NaiveDate.Range(
      NaiveDate.parse(snapshot.columnRange.start),
      NaiveDate.parse(snapshot.columnRange.end),
    ),
    timescale: snapshot.timescale,
    cells,
    rowOverlays,
    legends,
    revision: snapshot.revision != null
      ? { tau: snapshot.revision.tau, epoch: snapshot.revision.epoch }
      : null,
    work: null,
  };
}

function deserializeColumn(c: SerializedColumn): ColumnKey {
  // Reconstruct the day-level PartialDate from its start ISO. This
  // works for day timescale; finer/coarser scales would need extra
  // metadata, which we can layer on if multi-scale snapshots become
  // a need.
  const start = NaiveDate.parse(c.pdStart);
  const pd = new PartialDate("day", start.yr, start.month1, start.day1);
  const utcStart = TimezoneRegion.UTC.toWallClock(
    pd.start.withTime(TimeOfDay.ZERO),
  ).toUtc();
  const utcEnd = TimezoneRegion.UTC.toWallClock(
    pd.end.withTime(TimeOfDay.ZERO),
  ).toUtc();
  return {
    offset: c.offset,
    pd,
    range: new DateTime.Range<Utc>(utcStart, utcEnd),
  };
}

function deserializeCell(
  sc: SerializedCell,
  column: ColumnKey,
  registry: RowLocalId.Registry,
  path: readonly string[],
): CellNode {
  const rowId = registry.resolve(RowKey.of(sc.rowKey));
  const bindings = stubBindings(sc.bindings, rowId, [...path, "bindings"]);
  return {
    key: { kind: "cell", row: rowId, column },
    rowIndex: sc.rowIndex,
    columnIndex: sc.columnIndex,
    columnDate: NaiveDate.parse(sc.columnDate),
    bindings,
    plan: stubPlan(sc.plan, rowId, sc.rowIndex),
    layout: sc.layout != null ? stubLayout(sc.layout) : null,
    content: sc.content != null ? deserializeContent(sc.content) : null,
    stamp: sc.stamp != null
      ? {
        address: sc.stamp.address ?? null,
        valuesVersion: sc.stamp.valuesVersion,
        // Default-on-read: an artifact older than attribute versions carries no `attrVersion`.
        attrVersion: sc.stamp.attrVersion ?? 0,
      }
      : null,
  };
}

/**
 * `raw` read back as a `Role`; a string that is not one throws
 * `WireDecodeError` (type `"SceneSnapshot"`) at `path`.
 */
function roleAt(raw: string, path: readonly string[]): Role {
  try {
    return Role.of(raw);
  } catch (error) {
    if (!(error instanceof WireDecodeError)) throw error;
    throw new WireDecodeError(
      "SceneSnapshot",
      error.issues.map((issue) => Model.issue([...path, ...issue.path], issue.code, issue.message)),
    );
  }
}

function stubBindings(
  b: SerializedBindings,
  rowId: RowLocalId,
  path: readonly string[],
): RenderBindings {
  const pluginRole = roleAt(b.pluginRole, [...path, "pluginRole"]);
  const rowRole = roleAt(b.rowRole, [...path, "rowRole"]);
  const make = (surface: RenderBinding["surface"]): RenderBinding => ({
    surface,
    requestedRowId: rowId,
    pluginRowId: rowId,
    sourceRowId: rowId,
    pluginId: b.pluginId,
    pluginRole,
    rowRole,
    contentType: b.contentType,
    bindingKind: "row-descriptor",
    boundRowId: null,
    boundRendererId: null,
  });
  const row = make("row");
  const cell = make("cell");
  const chrome = make("chrome");
  return { row, cell, chrome, capabilities: [row, cell, chrome] };
}

/** A serialized span-stacked plan's timescale; throws on one that is not a `Timescale`. */
function spanTimescaleOf(raw: Option<string>): Timescale {
  const timescale = Timescale.parse(raw);
  if (timescale == null) {
    throw new Error(`deserializeScene: span timescale ${JSON.stringify(raw)} is not a timescale`);
  }
  return timescale;
}

function stubPlan(p: SerializedPlan, rowId: RowLocalId, rowIndex: number): RenderPlan {
  const mode = p.mode.type === "span-stacked"
    ? {
      type: "span-stacked" as const,
      spanTimescale: spanTimescaleOf(p.mode.spanTimescale ?? null),
      spanCount: p.mode.spanCount ?? 0,
    }
    : p.mode.type === "spanned"
    ? { type: "spanned" as const, isStatic: true }
    : p.mode.type === "stacked"
    ? { type: "stacked" as const }
    : { type: "normal" as const };
  return {
    rowId,
    descriptorType: p.descriptorType,
    rowIndex,
    updateType: "full-rebuild",
    diff: null,
    mode,
    timeState: p.timeState,
    layout: { offsetPx: 0, cellHeightPx: 0, cellHeightWithChildrenPx: 0, options: null, stackingInfo: null },
    spanMode: { type: p.spanMode.type as "none" | "hidden" },
    skipContent: p.skipContent,
    stacking: p.stackingSubcount > 0
      // Subcolumns aren't preserved in the snapshot; only the
      // length is needed by `diffCellNode`. Stub with an array of
      // the right size — the entries don't need full ColumnKey
      // shape because the diff classifier only reads `.length`.
      ? { subColumns: new Array(p.stackingSubcount).fill({}) as ColumnKey[] }
      : null,
  };
}

function stubLayout(l: SerializedLayout): CellLayout {
  return {
    offsetPx: l.offsetPx,
    cellHeightPx: l.cellHeightPx,
    cellHeightWithChildrenPx: l.cellHeightWithChildrenPx,
    options: null,
    stackingInfo: l.stackingCellsPerColumn != null
      ? {
        cellsPerColumn: l.stackingCellsPerColumn,
        // Defaults — `cellLayoutEqual` doesn't compare these, but
        // they're required by `StackingInfo`'s shape.
        individualCellHeight: 0,
        visibleCellCount: l.stackingCellsPerColumn,
        seeMoreHeight: 0,
      }
      : null,
  };
}

function deserializeContent(c: SerializedContent): CellContent {
  if (c.kind === "none") return { kind: "none" };
  if (c.entry == null) return { kind: "value", entry: null };
  // The serialized date is `PartialDate.toString()`; parse it back (the old
  // cast handed the `Result` itself on as the date).
  const date = PartialDate.parse(c.entry.date).asOk();
  if (date == null) {
    throw new Error(`deserializeScene: cell date "${c.entry.date}" is not a partial date`);
  }
  return {
    kind: "value",
    entry: {
      id: c.entry.id == null ? null : CellId.of(c.entry.id),
      date,
      // Read back as the CellValue its JSON is; a value no CellValue holds
      // (a list of strings) restores as `null` — the diff then reports the
      // cell changed, never a wrong value.
      value: cellValueFromJson(c.entry.value)?.value ?? null,
      style: null,
    },
  };
}

function deserializeOverlay(
  so: SerializedOverlay,
  registry: RowLocalId.Registry,
  path: readonly string[],
): RowOverlayNode {
  const rowId = registry.resolve(RowKey.of(so.rowKey));
  return {
    key: { kind: "row-overlay", row: rowId, pluginId: so.pluginId },
    rowIndex: so.rowIndex,
    bindings: stubBindings(so.bindings, rowId, [...path, "bindings"]),
    intents: so.intents.map(deserializeIntent),
  };
}

function deserializeIntent(i: SerializedIntent): FixedChildIntent {
  return {
    id: i.id,
    range: new NaiveDate.Range(
      NaiveDate.parse(i.rangeStart),
      NaiveDate.parse(i.rangeEnd),
    ),
    pluginId: i.pluginId,
    ...(i.align === "start" || i.align === "end" || i.align === "center"
      ? { align: i.align }
      : {}),
    ...(i.fillWidth != null ? { fillWidth: i.fillWidth } : {}),
    ...(i.isOverflow != null ? { isOverflow: i.isOverflow } : {}),
  };
}

function deserializeLegend(
  sl: SerializedLegend,
  registry: RowLocalId.Registry,
  path: readonly string[],
): LegendNode {
  const rowId = registry.resolve(RowKey.of(sl.rowKey));
  return {
    key: { kind: "legend", row: rowId },
    rowIndex: sl.rowIndex,
    bindings: stubBindings(sl.bindings, rowId, [...path, "bindings"]),
    label: sl.label,
    timescaleSetting: sl.timescaleSetting,
    isSystemRow: sl.isSystemRow,
    section: sl.section,
  };
}
